import type { ReactNode } from 'react';
import { cn } from './cn.js';
import { Icon } from './Icon.js';

export interface Destination {
  id: string;
  name: string;
}

/**
 * A coluna do movimento da `DeskModal`: para onde o registro pode ir, o que
 * falta para ir, e o botão.
 *
 * Os destinos vêm em grupos (`DestinationGroup`), e **só os permitidos**: a
 * lista sai da regra do módulo — no sinistro, o `allowed_moves` do pipe —, e
 * nenhuma tela escreve à mão para onde um registro pode ir.
 *
 * **A tela não sugere destino.** Um resultado de fase poderia apontar o
 * próximo, mas enquanto a regra não disser qual resposta leva a qual fase, a
 * escolha é de quem opera (regra 14).
 *
 * O rodapé tem duas peças: o `status`, um selo curto com o resumo do que falta
 * ("6 obrigatórios pendentes", "Pronto para avançar"), e a `action`, o botão
 * que diz o destino escolhido ("Salvar e mover para Aguardando Retorno"). O
 * botão **não fica desabilitado por falta de campo** (regra 1 dos formulários
 * do sinistro): clicar com pendência mostra o que falta, e desabilitar
 * esconderia o porquê.
 */
export function TransitionPanel({
  label = 'Movimento',
  children,
  status,
  action,
}: {
  label?: string;
  /** Os `DestinationGroup` e, embaixo deles, o `Checklist` do que falta. */
  children: ReactNode;
  status?: ReactNode;
  action: ReactNode;
}) {
  return (
    <aside
      aria-label={label}
      className="flex min-w-0 flex-col gap-4 border-t border-borda-suave bg-superficie-suave px-4 py-4 lg:overflow-y-auto lg:border-t-0 lg:border-l"
    >
      {children}
      {/* O botão diz o destino ("Salvar e mover para Ag. Docs e Ass. Termo"),
          e o nome da fase não cabe numa linha de 18rem: aqui ele quebra, em vez
          de vazar da coluna como o `whitespace-nowrap` do `Button` faria. */}
      <div className="mt-auto flex flex-col gap-2 [&>button]:h-auto [&>button]:min-h-9 [&>button]:py-2 [&>button]:whitespace-normal">
        {status}
        {action}
      </div>
    </aside>
  );
}

/**
 * Um grupo de destinos — "Avançar para", "Voltar para". O grupo diz a
 * direção, e a seta de cada botão repete; um destino escolhido fica marcado, e
 * clicar nele de novo desmarca.
 *
 * Grupo vazio não aparece: "Voltar para" sem nada embaixo é um título que
 * promete o que não há.
 */
export function DestinationGroup({
  title,
  direction,
  destinations,
  selected,
  onSelect,
}: {
  title: string;
  direction: 'forward' | 'back';
  destinations: readonly Destination[];
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  if (destinations.length === 0) return null;
  return (
    <div role="group" aria-label={title} className="flex flex-col gap-1.5">
      <h3 className="text-xs font-semibold tracking-wide text-texto-tenue uppercase">
        {title}
      </h3>
      {destinations.map((d) => (
        <button
          key={d.id}
          type="button"
          aria-pressed={selected === d.id}
          onClick={() => onSelect(d.id)}
          className={cn(
            'flex min-h-11 items-center gap-2 rounded-borda border px-3 py-2 text-left text-sm font-semibold',
            selected === d.id
              ? 'border-primaria bg-primaria-suave'
              : 'border-borda bg-superficie hover:border-borda-forte active:bg-superficie-suave',
          )}
        >
          <span className="min-w-0 flex-1">{d.name}</span>
          <Icon
            nome={direction === 'forward' ? 'seta-direita' : 'seta-esquerda'}
            size={16}
            className="shrink-0 text-texto-tenue"
          />
        </button>
      ))}
    </div>
  );
}
